import { z } from 'zod';

import { HostStatus } from './host';
import { BrandNotificationSchema } from './settings';
import { BRAND_MEMBERSHIP_PERMISSIONS } from '../../access-control/permission';
import { DateStringSchema, EmailStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema, PhoneNumberValidationSchema } from '../common';
import { StripeAccountStatus } from '../payment-method/const';
import { BrandMembershipRole, UserAddressSchema, UserImageSizeMediaSchema } from '../user/user';

export const UpdateBrandMemberRoleRequestSchema = z
  .object({
    role: z.nativeEnum(BrandMembershipRole),
    permissions: z.array(z.enum(BRAND_MEMBERSHIP_PERMISSIONS)),
  })
  .strict();

export const UpdateBrandMemberRoleEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, memberId }: { brandId: string; memberId: string }) => `v1/brands/${brandId}/members/${memberId}/role`,
  responseSchema: z.void(),
  requestSchema: UpdateBrandMemberRoleRequestSchema,
});

export const InviteBrandMemberRequestSchema = z
  .object({
    firstName: z.string().nonempty(),
    lastName: z.string().nonempty(),
    emailAddress: EmailStringSchema,
    phoneNumber: PhoneNumberValidationSchema.optional(),
    permissions: z.array(z.enum(BRAND_MEMBERSHIP_PERMISSIONS)).optional(),
    // Points of contact: free-string title (UI-constrained by the dashboard `USER_TITLE_OPTIONS`),
    // persisted so an invited member can be tagged Finance / IT / Marketing at creation. The api
    // thread-through lands in PR4.
    title: z.string().nullable().optional(),
  })
  .strict();

export const InviteBrandMemberEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/members`,
  responseSchema: z.void(),
  requestSchema: InviteBrandMemberRequestSchema,
});

export const GetBrandMembersQuerySchema = paginateQuerySchema({
  sortBy: ['name', 'email', 'phone', 'status', 'totalEarning'],
}).extend({
  onlyHosts: z.literal('true').optional(),
  status: z.nativeEnum(HostStatus).optional(),
});

export const BrandMemberSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: EmailStringSchema,
  phoneNumber: PhoneNumberValidationSchema.nullable(),
  dateOfBirth: DateStringSchema.nullable(),
  role: z.string(), // use the enum?
  address: UserAddressSchema.nullable(),
  description: z.string().nullable(),
  profilePicture: UserImageSizeMediaSchema.nullable(),
  commissionPercentage: z.number().nullable(),
  ratings: z.object({ rating: z.number(), recommendability: z.number(), total: z.number().optional() }).optional(),
  membershipRole: z.nativeEnum(BrandMembershipRole),
  hasFutureConfirmedSession: z.boolean(),
  isConfirmed: z.boolean(),
  stripeAccountStatus: z.nativeEnum(StripeAccountStatus),
  confirmedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  status: z.nativeEnum(HostStatus),
  totalEarning: z.number(),
  permissions: z.array(z.enum(BRAND_MEMBERSHIP_PERMISSIONS)),
  settings: z
    .object({
      isCurated: z.boolean().optional(),
      notifications: BrandNotificationSchema.optional(),
    })
    .nullable(),
});

export const GetBrandMembersEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.infer<typeof GetBrandMembersQuerySchema> }) =>
    `v1/brands/${brandId}/members?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(BrandMemberSchema),
  requestSchema: z.void(),
  querySchema: GetBrandMembersQuerySchema,
});

const UpdateBrandMemberRequestSchema = z.object({
  permissions: z.array(z.enum(BRAND_MEMBERSHIP_PERMISSIONS)),
});

export const UpdateBrandMemberPermissionsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, memberId }: { brandId: string; memberId: string }) =>
    `v1/brands/${brandId}/members/${memberId}/permissions`,
  responseSchema: z.void(),
  requestSchema: UpdateBrandMemberRequestSchema,
});
